import React, { useEffect, useRef, useState } from 'react'
import {
  BaseFieldPanel,
  getPromptPlaceholder,
  isSmartFieldType,
} from './BaseFieldPanel'
import { getInternalType } from './types'

export interface AddFieldPanelProps {
  onBack: () => void
  onClose: () => void
  onShowFieldType: () => void
  showFieldType: boolean
  showAddField: boolean
  onHideFieldType: () => void
  fieldTypePanelRef: React.RefObject<HTMLDivElement>
  addFieldPanelRef: React.RefObject<HTMLFormElement>
  position: 'left' | 'right'
  onTypeChange: (type: string) => void
  onAddField?: (fieldData: {
    name: string
    type: string
    aiAutofill?: boolean
    prompt?: string
    position: 'left' | 'right'
  }) => Promise<void>
  isAddingField?: boolean
}

export function AddFieldPanel({
  onBack,
  onClose,
  onShowFieldType,
  showFieldType,
  showAddField,
  onHideFieldType,
  fieldTypePanelRef,
  addFieldPanelRef,
  position,
  onTypeChange,
  onAddField,
  isAddingField = false,
}: AddFieldPanelProps) {
  const [aiAutofill, setAiAutofill] = useState(false)
  const [selectedFieldType, setSelectedFieldType] = useState('Text')
  const [fieldName, setFieldName] = useState('')
  const [promptValue, setPromptValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const fieldNameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (showAddField && !showFieldType) {
      fieldNameInputRef.current?.focus()
    }
  }, [showAddField, showFieldType])

  const handleTypeSelect = (type: string) => {
    setSelectedFieldType(type)
    onHideFieldType()
  }

  const handleAddColumn = async () => {
    if (!fieldName.trim()) {
      setError('Field name is required')
      return
    }

    if (isSmartFieldType(selectedFieldType) && !promptValue.trim()) {
      setError('Prompt is required for smart field types')
      return
    }

    setError(null)

    if (onAddField) {
      try {
        await onAddField({
          name: fieldName.trim(),
          type: getInternalType(selectedFieldType),
          aiAutofill,
          prompt: promptValue.trim() || undefined,
          position,
        })
        onClose()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add field')
      }
    } else {
      // Fallback to old behavior
      const internalType = getInternalType(selectedFieldType)
      onTypeChange(internalType)
      onClose()
    }
  }

  const isSmartType = isSmartFieldType(selectedFieldType)
  const hasChanges =
    fieldName.trim() !== '' ||
    aiAutofill ||
    (isSmartType && promptValue.trim() !== '')

  return (
    <BaseFieldPanel
      onBack={onBack}
      onClose={onClose}
      onShowFieldType={onShowFieldType}
      showFieldType={showFieldType}
      onHideFieldType={onHideFieldType}
      fieldTypePanelRef={fieldTypePanelRef}
      panelRef={addFieldPanelRef}
      title={`Add column ${position}`}
      selectedFieldType={selectedFieldType}
      fieldName={fieldName}
      onFieldNameChange={setFieldName}
      onTypeSelect={handleTypeSelect}
      onSave={handleAddColumn}
      isSaving={isAddingField}
      saveButtonText='Add column'
      hasChanges={hasChanges}
      error={error}
      onErrorClear={() => setError(null)}
      aiAutofill={aiAutofill}
      onAiAutofillChange={setAiAutofill}
      promptValue={promptValue}
      onPromptChange={setPromptValue}
      showPromptInput={isSmartType}
      getPromptPlaceholder={() => getPromptPlaceholder(selectedFieldType)}
      fieldRef={fieldNameInputRef}
    />
  )
}
